import React, { useState, useEffect } from 'react';
import { weatherAPI } from '../../services/api';
import styles from './Weather.module.css';

const Weather = () => {
  const [currentWeather, setCurrentWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState({
    name: 'Marienplatz, München',
    lat: 48.1374,
    lon: 11.5755
  });

  // München locations for quick selection - Phase 6: Updated to München
  const locations = [
    { name: 'Marienplatz, München', lat: 48.1374, lon: 11.5755 },
    { name: 'Schwabing, München', lat: 48.1645, lon: 11.5813 },
    { name: 'Maxvorstadt, München', lat: 48.1520, lon: 11.5673 },
    { name: 'Isarvorstadt, München', lat: 48.1308, lon: 11.5646 },
    { name: 'Allianz Arena, München', lat: 48.2188, lon: 11.6248 }
  ];

  useEffect(() => {
    loadWeatherData();
  }, [selectedLocation]);

  const loadWeatherData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load current weather
      const currentResponse = await weatherAPI.getCurrentWeather(
        selectedLocation.lat, 
        selectedLocation.lon
      );

      if (currentResponse.success) {
        setCurrentWeather(currentResponse.data);
      }

      // Load forecast
      const forecastResponse = await weatherAPI.getWeatherForecast(
        selectedLocation.lat, 
        selectedLocation.lon
      );

      if (forecastResponse.success) {
        setForecast(forecastResponse.data.forecast || []);
      }

    } catch (err) {
      console.error('Weather loading error:', err);
      setError('Wetterdaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
  };

  const getWeatherIcon = (iconCode) => {
    const iconMap = {
      '01d': '☀️', '01n': '🌙',
      '02d': '⛅', '02n': '☁️',
      '03d': '☁️', '03n': '☁️',
      '04d': '☁️', '04n': '☁️',
      '09d': '🌧️', '09n': '🌧️',
      '10d': '🌦️', '10n': '🌧️',
      '11d': '⛈️', '11n': '⛈️',
      '13d': '❄️', '13n': '❄️',
      '50d': '🌫️', '50n': '🌫️'
    };
    return iconMap[iconCode] || '🌤️';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'VERY_HIGH': '#ff4444',
      'HIGH': '#ff8800',
      'MEDIUM_HIGH': '#ffaa00',
      'MEDIUM': '#ffcc00',
      'NORMAL': '#4CAF50',
      'LOW': '#2196F3'
    };
    return colors[priority] || '#666';
  };

  const formatTime = (dtText) => {
    return new Date(dtText).toLocaleDateString('de-DE', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className={styles.weather}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Wetterdaten werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.weather}>
      <div className={styles.header}>
        <h1>🌤️ Wetter & Taxi-Nachfrage</h1>
        <p>Wetterbasierte Nachfrageprognosen für Taxifahrer</p>
      </div>

      {/* Location Selector */}
      <div className={styles.locationSelector}>
        <h3>📍 Standort auswählen:</h3>
        <div className={styles.locationButtons}>
          {locations.map((location) => (
            <button
              key={location.name}
              className={`${styles.locationBtn} ${
                selectedLocation.name === location.name ? styles.active : ''
              }`}
              onClick={() => handleLocationChange(location)}
            >
              {location.name}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          ⚠️ {error}
        </div>
      )}

      {/* Current Weather */}
      {currentWeather && (
        <div className={styles.currentWeather}>
          <h2>Aktuelles Wetter - {selectedLocation.name}</h2>
          <div className={styles.currentCard}>
            <div className={styles.weatherMain}>
              <div className={styles.icon}>
                {getWeatherIcon(currentWeather.weather.weather[0].icon)}
              </div>
              <div className={styles.temp}>
                {Math.round(currentWeather.weather.main.temp)}°C
              </div>
              <div className={styles.description}>
                {currentWeather.weather.weather[0].description}
              </div>
            </div>
            
            <div className={styles.weatherDetails}>
              <div className={styles.detail}>
                <span>Gefühlt:</span>
                <span>{Math.round(currentWeather.weather.main.feels_like)}°C</span>
              </div>
              <div className={styles.detail}>
                <span>Luftfeuchtigkeit:</span>
                <span>{currentWeather.weather.main.humidity}%</span>
              </div>
              <div className={styles.detail}>
                <span>Wind:</span>
                <span>{currentWeather.weather.wind.speed} m/s</span>
              </div>
              <div className={styles.detail}>
                <span>Sichtweite:</span>
                <span>{(currentWeather.weather.visibility / 1000).toFixed(1)} km</span>
              </div>
            </div>

            {/* Taxi Recommendation */}
            <div className={styles.taxiRecommendation}>
              <h3>🚖 Taxi-Nachfrage Prognose</h3>
              <div 
                className={styles.demandIndicator}
                style={{ backgroundColor: getPriorityColor(currentWeather.taxiRecommendation.priority) }}
              >
                <div className={styles.demandFactor}>
                  {currentWeather.taxiRecommendation.demandFactor}x
                </div>
                <div className={styles.demandMessage}>
                  {currentWeather.taxiRecommendation.message}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weather Forecast */}
      {forecast.length > 0 && (
        <div className={styles.forecast}>
          <h2>📅 3-Tage Wettervorhersage</h2>
          <div className={styles.forecastGrid}>
            {forecast.map((item, index) => (
              <div key={index} className={styles.forecastCard}>
                <div className={styles.forecastDate}>
                  {formatTime(item.dt_txt)}
                </div>
                <div className={styles.forecastIcon}>
                  {getWeatherIcon(item.weather[0].icon)}
                </div>
                <div className={styles.forecastTemp}>
                  {Math.round(item.main.temp)}°C
                </div>
                <div className={styles.forecastDesc}>
                  {item.weather[0].description}
                </div>
                <div 
                  className={styles.forecastDemand}
                  style={{ color: getPriorityColor(item.taxiRecommendation.priority) }}
                >
                  🚖 {item.taxiRecommendation.demandFactor}x
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weather Tips */}
      <div className={styles.tips}>
        <h3>💡 Wetter-Tipps für Taxifahrer</h3>
        <div className={styles.tipsList}>
          <div className={styles.tip}>
            <span className={styles.tipIcon}>🌧️</span>
            <span>Bei Regen: 80% mehr Nachfrage! Besonders bei Bahnhöfen und Einkaufszentren positionieren.</span>
          </div>
          <div className={styles.tip}>
            <span className={styles.tipIcon}>❄️</span>
            <span>Bei Schnee: Bis zu 120% mehr Nachfrage! Früh am Morgen strategisch positionieren.</span>
          </div>
          <div className={styles.tip}>
            <span className={styles.tipIcon}>☀️</span>
            <span>Bei schönem Wetter: Normale Nachfrage, touristiche Ziele bevorzugen.</span>
          </div>
          <div className={styles.tip}>
            <span className={styles.tipIcon}>⛈️</span>
            <span>Bei Gewitter: Hohe Nachfrage! Überdachte Warteplätze anfahren.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Weather;
